import { useState, useEffect, useCallback } from 'react';
import {
  assistScene,
  listCharacters,
  listSceneVersions,
  createSceneVersion,
  updateScene,
  type ContentProject,
  type ContentScene,
  type ContentCharacter,
  type AssistResult,
} from '@/lib/contentStudio';

interface Props {
  project: ContentProject;
  scenes: ContentScene[];
  onScenesChanged: () => void;
}

interface Turn {
  id: number;
  instruction: string;
  sceneNumber: number | null;
  result: AssistResult | null;
  error?: string;
  applied?: boolean;
}

const SUGGESTIONS = [
  'Rends cette scène plus cinématographique',
  'Raccourcis cette scène',
  'Ajoute une transition plus fluide',
  'Rends cette introduction plus captivante',
  'Change seulement la voix',
];

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Assistant créatif IA du projet. Il connaît la Bible du projet et les
 * personnages persistants, et applique une modification CIBLÉE sur une seule
 * scène (les autres ne bougent pas). Chaque application crée une nouvelle
 * version de scène (l'ancienne est conservée).
 */
export default function CreativeAssistantPanel({ project, scenes, onScenesChanged }: Props) {
  const [characters, setCharacters] = useState<ContentCharacter[]>([]);
  const [targetSceneId, setTargetSceneId] = useState<string>(scenes[0] ? String(scenes[0].id) : '');
  const [instruction, setInstruction] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [busy, setBusy] = useState(false);
  const [applyingId, setApplyingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadCharacters = useCallback(async () => {
    try {
      setCharacters(await listCharacters(project.id));
    } catch {
      /* le contexte personnages est optionnel */
    }
  }, [project.id]);

  useEffect(() => { loadCharacters(); }, [loadCharacters]);

  useEffect(() => {
    if (!scenes.some((s) => String(s.id) === targetSceneId)) {
      setTargetSceneId(scenes[0] ? String(scenes[0].id) : '');
    }
  }, [scenes, targetSceneId]);

  const targetScene = scenes.find((s) => String(s.id) === targetSceneId) || null;

  const handleAsk = async () => {
    const text = instruction.trim();
    if (!text || busy) return;
    if (!targetScene) { setError('Sélectionnez une scène à modifier.'); return; }
    setBusy(true);
    setError(null);
    try {
      const result = await assistScene({
        instruction: text,
        language: project.language,
        style: project.style || undefined,
        bible: (project.bible || null) as never,
        characters,
        scene: {
          script: targetScene.script,
          visual_prompt: targetScene.visual_prompt,
          narrative_prompt: targetScene.narrative_prompt,
          voiceover: targetScene.voiceover,
          duration: targetScene.duration,
        },
      });
      setTurns((prev) => [
        { id: Date.now(), instruction: text, sceneNumber: targetScene.number, result },
        ...prev,
      ]);
      setInstruction('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'L\u2019assistant a échoué');
    } finally {
      setBusy(false);
    }
  };

  const applyTurn = async (turn: Turn) => {
    if (!turn.result || turn.sceneNumber == null) return;
    const scene = scenes.find((s) => s.number === turn.sceneNumber);
    if (!scene) return;
    setApplyingId(turn.id);
    setError(null);
    try {
      const nextVisual = turn.result.visual_prompt || scene.visual_prompt || '';
      const nextScript = turn.result.script || scene.script || '';
      const nextVoice = turn.result.voiceover || scene.voiceover || '';
      // Historique : nouvelle version de scène (l'ancienne n'est jamais supprimée).
      const versions = await listSceneVersions(scene.id);
      const nextVersion = (versions[0]?.version || 0) + 1;
      const v = await createSceneVersion(scene.id, nextVisual, nextVersion);
      await updateScene(scene.id, {
        script: nextScript,
        visual_prompt: nextVisual,
        narrative_prompt: turn.result.narrative_prompt || scene.narrative_prompt || null,
        voiceover: nextVoice,
        status: 'ready',
        active_version_id: v?.id ?? scene.active_version_id,
      });
      setTurns((prev) => prev.map((t) => (t.id === turn.id ? { ...t, applied: true } : t)));
      onScenesChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Application impossible');
    } finally {
      setApplyingId(null);
    }
  };

  const contextChips = [
    project.style ? `Style : ${project.style}` : '',
    (project.bible as Record<string, string> | null)?.style ? `Visuel : ${(project.bible as Record<string, string>).style}` : '',
    characters.length ? `${characters.length} personnage${characters.length > 1 ? 's' : ''}` : '',
  ].filter(Boolean);

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-base font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-sparkling-2-line text-accent-500"></i>Assistant créatif
          </h3>
          <p className="text-xs text-foreground-500 mt-1 max-w-2xl">
            Demandez une modification ciblée sur une scène. L&apos;assistant s&apos;appuie sur la <strong>Bible du projet</strong> et
            vos <strong>personnages</strong> pour garder la cohérence, et ne touche que la scène choisie.
          </p>
        </div>
      </div>

      {contextChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-[11px] text-foreground-400">Contexte pris en compte :</span>
          {contextChips.map((c) => (
            <span key={c} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800 text-[11px] font-medium">
              <i className="ri-links-line"></i>{c}
            </span>
          ))}
        </div>
      )}

      {error && (
        <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
          <i className="ri-error-warning-line"></i>{error}
        </div>
      )}

      {/* Zone de conversation */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          <div className="sm:col-span-1">
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Scène à modifier</label>
            <div className="relative">
              <select
                value={targetSceneId}
                onChange={(e) => setTargetSceneId(e.target.value)}
                className="w-full appearance-none px-3 py-2 pr-8 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-300 cursor-pointer"
              >
                {scenes.length === 0 && <option value="">Aucune scène</option>}
                {scenes.map((s) => <option key={s.id} value={s.id}>Scène {pad(s.number)}</option>)}
              </select>
              <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Votre demande</label>
            <div className="flex items-center gap-2">
              <input
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAsk(); } }}
                placeholder="Ex : Rends cette scène plus cinématographique"
                className="flex-1 min-w-0 px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-300"
              />
              <button
                type="button"
                onClick={handleAsk}
                disabled={busy || !instruction.trim() || !targetScene}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
              >
                {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-2-line"></i>}
                Demander
              </button>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setInstruction(s)}
              className="px-3 py-1.5 rounded-full border border-background-200/70 text-xs text-foreground-600 hover:bg-background-100 hover:border-accent-300 transition-colors cursor-pointer"
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Scène cible */}
      {targetScene && (
        <div className="mb-4 bg-background-100/60 border border-background-200/70 rounded-xl p-3">
          <p className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wider mb-1.5">Scène {pad(targetScene.number)} — contexte actuel</p>
          <p className="text-xs text-foreground-600 line-clamp-2 mb-1"><span className="font-medium text-foreground-700">Script : </span>{targetScene.script || '—'}</p>
          <p className="text-xs text-foreground-600 line-clamp-2"><span className="font-medium text-foreground-700">Prompt visuel : </span>{targetScene.visual_prompt || '—'}</p>
        </div>
      )}

      {/* Historique des demandes */}
      {turns.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 bg-background-50 border border-background-200/70 rounded-xl text-center">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-sparkling-2-line text-xl text-foreground-400"></i>
          </div>
          <p className="text-sm font-medium text-foreground-700">Posez votre première demande</p>
          <p className="text-xs text-foreground-500 mt-1 max-w-xs">L&apos;assistant proposera une modification que vous pourrez appliquer ou non.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {turns.map((t) => (
            <div key={t.id} className="bg-background-50 border border-background-200/70 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 flex-shrink-0"><i className="ri-user-3-line text-xs"></i></span>
                <span className="text-xs font-medium text-foreground-800">{t.instruction}</span>
                {t.sceneNumber != null && <span className="ml-auto text-[11px] text-foreground-400 whitespace-nowrap">Scène {pad(t.sceneNumber)}</span>}
              </div>
              {t.result?.note && (
                <p className="text-xs text-accent-700 bg-accent-50 rounded-md px-2.5 py-1.5 mb-2 flex items-start gap-1.5">
                  <i className="ri-lightbulb-line mt-0.5"></i>{t.result.note}
                </p>
              )}
              {t.result && (
                <div className="space-y-1.5 text-xs text-foreground-600">
                  {t.result.visual_prompt && (
                    <p><span className="font-medium text-foreground-700">Prompt visuel : </span>{t.result.visual_prompt}</p>
                  )}
                  {t.result.script && (
                    <p className="line-clamp-3"><span className="font-medium text-foreground-700">Script : </span>{t.result.script}</p>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2 mt-3">
                {t.applied ? (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-accent-700">
                    <i className="ri-checkbox-circle-line"></i>Appliqué — nouvelle version de scène créée
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => applyTurn(t)}
                    disabled={applyingId === t.id || !t.result}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
                  >
                    {applyingId === t.id ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                    Appliquer à la scène
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setTurns((prev) => prev.filter((x) => x.id !== t.id))}
                  className="ml-auto text-[11px] text-foreground-400 hover:text-red-500 transition-colors cursor-pointer"
                >
                  Ignorer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}